import { useAtomValue, useSetAtom } from 'jotai'
import { LucideExternalLink, LucideX } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { aClient, aSelectedListing } from '~/atoms'
import { SelectionCheckbox } from '~/components/common'
import { ListingGallery, ListingMeta } from '~/components/listing'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui'
import useMapListingLink from '~/hooks/map/useMapListingLink'
import { useListingLink } from '~/hooks/misc'
import { cn, fmtAddress } from '~/lib'
import type { Listing, ParcelFeature } from '~/models'
import { ListingInfo, ListingTabs } from './preview'

type MapPreviewListingProps = {
  className?: string
  listing: Listing
  parcelFeatures: ParcelFeature[]
}

export default function MapPreviewListing(props: MapPreviewListingProps) {
  const { listing, className, parcelFeatures } = props
  const navigate = useNavigate()
  const location = useLocation()
  const setSelectedListing = useSetAtom(aSelectedListing)
  const client = useAtomValue(aClient)
  const parcels = useMemo(
    () => parcelFeatures.map((feature) => feature.properties),
    [parcelFeatures]
  )
  const parcelDataEnabled = client?.parcel_data_enabled === true

  const sourceUrl = useListingLink(listing)
  const listingLink = useMapListingLink(listing)
  const listingLabel = listing.name || listing.property.address || 'listing'
  const listingAddress = listing.property.address
    ? fmtAddress(listing.property, true)
    : null
  const listingAccessibleLabel =
    listingAddress && listingAddress !== listingLabel
      ? `${listingLabel} at ${listingAddress}`
      : listingLabel

  return (
    <div
      className={cn(
        'flex min-h-full select-text flex-col bg-background pb-8 md:pb-0 lg:rounded-md lg:shadow-md',
        className
      )}>
      <div className="relative">
        <SelectionCheckbox listing={listing} />
        <Button
          className="absolute right-2 top-2 z-10 opacity-75 shadow-md transition-opacity duration-200 hover:!opacity-100"
          variant="map"
          size="map-sm"
          aria-label="Close preview"
          onClick={() => {
            // if (!isReportPage) {
            navigate(`/${location.search}`)
            // }
            setSelectedListing(null)
          }}>
          <LucideX className="h-5 w-5" />
        </Button>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="map"
                size="map-sm"
                className="absolute right-12 top-2 z-10 opacity-75 shadow-md transition-opacity duration-200 hover:!opacity-100">
                <Link
                  to={listingLink}
                  target="_blank"
                  aria-label={`Open listing ${listingAccessibleLabel} in a new tab`}>
                  <LucideExternalLink className="h-4 w-4" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Open in new tab</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {sourceUrl && (
          <Button
            asChild
            variant="map"
            size="sm"
            className="absolute bottom-2 right-2 z-10 opacity-75 shadow-md transition-opacity duration-200 hover:!opacity-100">
            <a href={sourceUrl} rel="noopener noreferrer" target="_blank">
              View Source
            </a>
          </Button>
        )}

        <ListingGallery key={listing._id} listing={listing} />
      </div>
      <div className="space-y-4 p-4 sm:p-6">
        <ListingInfo listing={listing} />
        <ListingMeta listing={listing} />
        <ListingTabs
          listing={listing}
          client={client!}
          parcels={parcelDataEnabled ? parcels : []}
        />
      </div>
    </div>
  )
}
